import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LogoLockup } from "./Logo";
import { WhatsAppButton } from "./WhatsAppButton";
import { scrollTopNow } from "../lib/smoothScroll";
import { NAV_LINKS, PHONE_DISPLAY, HOURS } from "../data/siteData";

const desktopLink = ({ isActive }) =>
  `text-[11px] font-mono-alt uppercase tracking-[0.22em] transition-colors duration-300 ${
    isActive ? "text-amber-300" : "text-stone-400 hover:text-amber-100"
  }`;

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const handleNavClick = (e, path) => {
    if (location.pathname === path) {
      e.preventDefault();
      scrollTopNow();
      setOpen(false);
    }
  };

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-amber-500/10 bg-[#0B0B0C]/85 backdrop-blur-xl"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
          <Link
            to="/"
            aria-label="Belong Salon & Spa — Home"
            data-testid="nav-logo-link"
            onClick={(e) => handleNavClick(e, "/")}
          >
            <LogoLockup />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.path === "/"}
                className={desktopLink}
                data-testid={l.testid}
                onClick={(e) => handleNavClick(e, l.path)}
              >
                {l.name}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <WhatsAppButton small testid="nav-whatsapp-booking-btn" />
          </div>

          <button
            className="text-amber-100 lg:hidden"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open-btn"
          >
            <Menu className="h-7 w-7" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[80] flex flex-col bg-[#0B0B0C] px-8 pb-10 pt-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="mobile-menu-overlay"
          >
            <div className="flex items-center justify-between">
              <button
                onClick={(e) => {
                  handleNavClick(e, "/");
                  if (location.pathname !== "/") navigate("/");
                }}
                aria-label="Belong Salon & Spa — Home"
                data-testid="mobile-nav-logo-link"
              >
                <LogoLockup compact />
              </button>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close-btn"
                className="text-amber-100"
              >
                <X className="h-7 w-7" />
              </button>
            </div>

            <nav className="mt-16 flex flex-col gap-2" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.5 }}
                >
                  <NavLink
                    to={l.path}
                    end={l.path === "/"}
                    data-testid={`mobile-${l.testid}`}
                    onClick={(e) => handleNavClick(e, l.path)}
                    className={({ isActive }) =>
                      `block border-b border-white/5 py-4 font-display text-4xl font-light transition-colors ${
                        isActive ? "text-gold-gradient" : "text-stone-300"
                      }`
                    }
                  >
                    {l.name}
                  </NavLink>
                </motion.div>
              ))}
            </nav>

            <motion.div
              className="mt-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <WhatsAppButton testid="mobile-whatsapp-booking-btn" className="w-full" />
              <p className="mt-5 text-center font-mono-alt text-[10px] uppercase tracking-[0.25em] text-stone-500">
                {PHONE_DISPLAY} · {HOURS}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
